<template>
    <validation-observer ref="form" v-slot="{ meta, setFieldValue, errors, values: formValues }">
        <modal
            @hidden="clearData"
            @shown="setData"
            :ok-disabled="!meta.valid"
            :id="modalId"
            :title="title"
            @ok.prevent="submit"
        >
            <div v-if="discountType === 0">
                <validation-provider name="name" rules="required">
                    <form-input
                        label="Name"
                        v-model="promoValues.name"
                        :error="errors['name']"
                        @input="(val) => setFieldValue('name', val)"
                    />
                </validation-provider>
                <div class="d-flex align-items-baseline input-wrapper justify-content-between flex-md-row flex-column">
                    <form-select
                        class="mb-md-3 mb-0 w-100"
                        label="Type"
                        :value="discountType"
                        disabled
                        :error="errors[0]"
                        :options="discountTypeOptions"
                    />
                    <validation-provider name="type" class="w-100" rules="required">
                        <form-select
                            class="mb-md-3 mb-0 w-100"
                            label="Value Type"
                            v-model="promoValues.type"
                            :error="errors.type"
                            :options="valueTypeOptions"
                            @change="(val) => setFieldValue('type', val)"
                        />
                    </validation-provider>
                    <form-select
                        label="Apply"
                        v-model="promoValues.automatic"
                        :error="errors.automatic"
                        :options="automaticOptions"
                        class="w-100"
                    />
                </div>
                <validation-provider name="value" rules="required|validNumber">
                    <form-input
                        :leftButton="promoValues.type !== 0"
                        :rightIcon="promoValues.type === 0 ? ['fas', 'percent'] : ''"
                        label="Value"
                        v-model="promoValues.value"
                        :error="errors.value"
                        @input="(val) => setFieldValue('value', val)"
                    >
                        <template v-if="promoValues.type !== 0" #leftButton>
                            <span> {{ userCurrencySymbol }}</span>
                        </template>
                    </form-input>
                </validation-provider>
                <div class="d-flex align-items-baseline input-wrapper justify-content-between flex-md-row flex-column">
                    <form-datepicker
                        class="w-100 mb-md-3 mb-0"
                        v-model="promoValues.start_date"
                        label="Travel Start Date:"
                        :hide-header="true"
                        :display-format="userUnicodeDateFormat"
                        @input="onChangeDate"
                    />
                    <validation-provider class="w-100" name="end_date">
                        <form-datepicker
                            :min="promoValues.start_date"
                            class="w-100"
                            v-model="promoValues.end_date"
                            label="Travel End Date:"
                            :hide-header="true"
                            :display-format="userUnicodeDateFormat"
                            :error="errors.end_date"
                            @input="onChangeDate"
                        />
                    </validation-provider>
                </div>
                <div v-if="!promoValues.automatic">
                    <form-input label="Coupon Code" v-model="promoValues.code" />
                    <div
                        class="d-flex align-items-baseline input-wrapper justify-content-between flex-md-row flex-column"
                    >
                        <form-datepicker
                            class="w-100 mb-md-3 mb-0"
                            v-model="promoValues.validity_start_date"
                            label="Validity Start Date:"
                            :hide-header="true"
                            :display-format="userUnicodeDateFormat"
                            @input="onChangeDate"
                        />
                        <validation-provider class="w-100" name="validity_end_date" as="div">
                            <form-datepicker
                                :min="promoValues.validity_start_date"
                                v-model="promoValues.validity_end_date"
                                label="Validity End Date:"
                                :hide-header="true"
                                :display-format="userUnicodeDateFormat"
                                :error="errors.validity_end_date"
                                @input="onChangeDate"
                            />
                        </validation-provider>
                    </div>
                    <form-select :options="minNightsOptions" label="Min Nights" v-model="promoValues.min_nights" />
                    <form-select :options="giftOptions" label="Gift Certificate" v-model="promoValues.max_use" />
                </div>
            </div>
            <div v-else>
                <validation-provider name="name" rules="required">
                    <form-input
                        label="Name"
                        v-model="values.name"
                        :error="errors.name"
                        @input="(val) => setFieldValue('name', val)"
                    />
                </validation-provider>
                <div class="d-flex align-items-baseline input-wrapper justify-content-between">
                    <form-select
                        label="Type"
                        v-model="values.dtype"
                        disabled
                        :error="errors[0]"
                        :options="discountTypeOptions"
                        class="w-100"
                    />
                    <validation-provider name="type" class="w-100" rules="required" as="div">
                        <form-select
                            label="Value Type"
                            v-model="values.type"
                            :error="errors.type"
                            :options="valueTypeOptions"
                            @change="(val) => setFieldValue('type', val)"
                        />
                    </validation-provider>
                    <form-select class="w-100" label="Apply" v-model="values.automatic" :options="automaticOptions" />
                </div>
                <div class="d-flex flex-column" v-if="values.automatic">
                    <form-select
                        label="Apply To Weekly Rates"
                        v-model="values.apply_to_weekly_rates"
                        :options="yesNoOptions"
                    />
                    <form-select
                        label="Apply To Monthly Rates"
                        v-model="values.apply_to_monthly_rates"
                        :options="yesNoOptions"
                    />
                </div>
            </div>
            <div v-if="discountType !== 0">
                <div
                    v-for="(item, i) in values.modifiers"
                    :key="i + 'modifiers'"
                    class="d-flex align-items-baseline input-wrapper justify-content-between"
                >
                    <validation-provider :name="`modifier[${i}]`" rules="required|validNumber">
                        <form-input
                            label="Values"
                            :leftButton="values.type !== 0"
                            :rightIcon="values.type === 0 ? ['fas', 'percent'] : ''"
                            v-model="item.value"
                            :error="errors[`modifier[${i}]`]"
                            @input="(val) => setFieldValue(`modifier[${i}]`, val)"
                        >
                            <template v-if="values.type !== 0" #leftButton>
                                <span> {{ userCurrencySymbol }}</span>
                            </template>
                        </form-input>
                    </validation-provider>
                    <span>for</span>
                    <form-select v-model="item.min_nights" class="w-50" :options="minNightsOptions" />
                    <span>nights</span>
                    <b-button @click="onAddModifier" variant="default">
                        <font-awesome-icon :icon="['fas', 'plus']"></font-awesome-icon>
                    </b-button>
                    <b-button :disabled="i === 0" @click="values.modifiers.splice(i, 1)" variant="default">
                        <font-awesome-icon :icon="['fas', 'trash']"></font-awesome-icon>
                    </b-button>
                </div>
            </div>
            <small> Edits will appear on all properties where this discount currently appears. </small>
        </modal>
    </validation-observer>
</template>

<script>
    import Modal from '../../generic/modal/modal.vue';
    import { mapGetters } from 'vuex';
    import formInput from '../../generic/form-input/form-input.vue';
    import formSelect from '../../generic/form-select/form-select.vue';
    import formDatepicker from '../../generic/form-datepicker/form-datepicker.vue';
    import isBefore from 'date-fns/isBefore';
    import parseISO from 'date-fns/parseISO';

    export default {
        name: 'AddEditDiscountModal',
        components: { Modal, formInput, formSelect, formDatepicker },
        props: {
            initialData: {
                type: Object,
                default: null,
            },
            modalId: {
                type: String,
            },
            discountType: {
                type: Number,
            },
        },
        data() {
            return {
                promoValues: {
                    name: 'New Discount',
                    type: 0,
                    automatic: false,
                    value: '',
                    start_date: null,
                    end_date: null,
                    code: '',
                    validity_start_date: null,
                    validity_end_date: null,
                    min_nights: 1,
                    max_use: 0,
                    dtype: 0,
                },
                values: {
                    name: 'new Discount',
                    type: 0,
                    value: '',
                    automatic: false,
                    apply_to_monthly_rates: false,
                    apply_to_weekly_rates: false,
                    dtype: this.discountType,
                    modifiers: [],
                },
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),
            title() {
                return this.initialData ? 'Edit Discount' : 'Add Discount';
            },
            discountTypeOptions() {
                // [{ n: 'Promotion', v: 0 }, { n: 'Length of Stay', v: 1 }, { n: 'Last Minute', v: 2 }]"
                return [
                    { text: 'Promotion', value: 0 },
                    { text: 'Length of Stay', value: 1 },
                    { text: 'Last Minute', value: 2 },
                ];
            },
            valueTypeOptions() {
                return [
                    { text: 'Fixed', value: 1 },
                    { text: 'Percent', value: 0 },
                ];
            },
            automaticOptions() {
                return [
                    { text: 'Automatic', value: true },
                    { text: 'Manual', value: false },
                ];
            },
            yesNoOptions() {
                return [
                    { text: 'Yes', value: true },
                    { text: 'No', value: false },
                ];
            },
            minNightsOptions() {
                return Array.from({ length: 90 }, (v, k) => k + 1).map((n) => {
                    return { text: n, value: n };
                });
            },
            giftOptions() {
                return [
                    {
                        text: 'Yes',
                        value: 1,
                    },
                    {
                        text: 'No',
                        value: 0,
                    },
                ];
            },
        },
        methods: {
            onAddModifier() {
                this.values.modifiers.push({ value: 0, min_nights: 1 });
                this.$refs.form.setFieldValue(`modifier[${this.values.modifiers.length - 1}]`, 0);
            },
            submit() {
                this.$emit(
                    'submit',
                    this.discountType === 0
                        ? { ...this.values, ...this.promoValues }
                        : { ...this.promoValues, ...this.values }
                );
            },
            getIsDateBefore(date1, date2) {
                return isBefore(parseISO(date1), parseISO(date2));
            },
            onChangeDate() {
                // validate date and set form as invalid
                if (this.getIsDateBefore(this.promoValues.validity_end_date, this.promoValues.validity_start_date)) {
                    this.$refs.form.setErrors({ validity_end_date: 'Invalid Date' });
                }
                if (this.getIsDateBefore(this.promoValues.end_date, this.promoValues.start_date)) {
                    this.$refs.form.setErrors({ end_date: 'Invalid Date' });
                }
            },
            setData() {
                if (this.initialData) {
                    this.values = {
                        ...this.initialData,
                        dtype: this.discountType,
                    };
                    this.promoValues = {
                        ...this.initialData,
                        dtype: this.discountType,
                    };
                } else {
                    this.values = {
                        name: 'New Discount',
                        type: 0,
                        value: 0,
                        automatic: false,
                        apply_to_monthly_rates: false,
                        apply_to_weekly_rates: false,
                        dtype: this.discountType,
                        modifiers: [{ value: 0, min_nights: 1 }],
                    };
                    this.promoValues = {
                        name: 'New Discount',
                        type: 0,
                        automatic: false,
                        value: 0,
                        start_date: null,
                        end_date: null,
                        code: '',
                        validity_start_date: null,
                        validity_end_date: null,
                        min_nights: 1,
                        max_use: 0,
                        dtype: this.discountType,
                    };
                }
                if (this.discountType === 0) {
                    this.$refs.form.setValues({
                        ...this.promoValues,
                    });
                } else {
                    this.$refs.form.setValues({
                        ...this.values,
                    });
                    this.values.modifiers.forEach((item, i) => {
                        this.$refs.form.setFieldValue(`modifier[${i}]`, item.value);
                    });
                }
            },
            clearData() {
                this.$emit('hide');
                this.values = {
                    name: 'New Discount',
                    type: 0,
                    value: '',
                    automatic: false,
                    apply_to_monthly_rates: false,
                    apply_to_weekly_rates: false,
                    dtype: this.discountType,
                    modifiers: [{ value: 0, min_nights: 1 }],
                };
                this.promoValues = {
                    name: 'New Discount',
                    type: 0,
                    automatic: false,
                    value: '',
                    start_date: null,
                    end_date: null,
                    code: '',
                    validity_start_date: null,
                    validity_end_date: null,
                    min_nights: 1,
                    max_use: 0,
                    dtype: this.discountType,
                };
            },
        },
        watch: {
            initialData() {
                this.setData();
            },
        },
    };
</script>

<style scoped>
    .input-wrapper {
        gap: 1rem;
    }
</style>
